import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Camera, Loader2, X, ScanLine, Sparkles, Save } from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Barcode scanner using the device camera + ZXing. Resolves the scanned
 * EAN/UPC against the backend (custom DB → Open Food Facts → optional AI
 * vision fallback). On confirmation it logs the meal via the existing
 * `/api/nutrition/food` endpoint and persists the product to the user's
 * custom DB for next time.
 *
 * Props:
 *   - open / onOpenChange: standard sheet controls
 *   - onLogged?: optional callback fired after a successful food log
 */
export function BarcodeScannerSheet({ open, onOpenChange, onLogged }) {
    const { language } = useLanguage();
    const videoRef = useRef(null);
    const readerRef = useRef(null);
    const fileInputRef = useRef(null);

    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState(null);
    const [product, setProduct] = useState(null); // resolved product
    const [unknownEan, setUnknownEan] = useState(null); // ean that wasn't found
    const [grams, setGrams] = useState(100);
    const [mealType, setMealType] = useState('snack');
    const [submitting, setSubmitting] = useState(false);
    const [manualEan, setManualEan] = useState('');
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiResult, setAiResult] = useState(null);
    // Visible diagnostic so we know where the camera bring-up is stuck.
    const [camStatus, setCamStatus] = useState('idle');

    const stopCamera = useCallback(() => {
        try {
            if (readerRef.current) {
                readerRef.current.reset?.();
                readerRef.current = null;
            }
            const v = videoRef.current;
            if (v && v.srcObject) {
                v.srcObject.getTracks().forEach(t => t.stop());
                v.srcObject = null;
            }
        } catch (_e) { /* ignore */ }
    }, []);

    const lookupEan = useCallback(async (ean) => {
        try {
            const res = await axios.get(`${API}/api/nutrition/barcode/${ean}`);
            if (res.data?.found && res.data?.product) {
                setProduct(res.data.product);
                setGrams(res.data.product.serving_quantity_g || 100);
                stopCamera();
                toast.success(language === 'de' ? 'Produkt erkannt!' : 'Product recognized!');
            } else {
                setUnknownEan(ean);
                stopCamera();
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || (language === 'de' ? 'Lookup fehlgeschlagen' : 'Lookup failed');
            toast.error(detail);
        }
    }, [language, stopCamera]);

    // Just flips scanning state on — the actual camera bring-up happens in
    // a separate effect that waits for the <video> element to be mounted.
    // This avoids a race where ZXing receives a null videoRef and silently
    // pipes the stream to an internal off-DOM element (camera works but our
    // <video> stays black on iOS).
    const startCamera = useCallback(() => {
        setError(null);
        setProduct(null);
        setUnknownEan(null);
        setAiResult(null);
        setScanning(true);
    }, []);

    useEffect(() => {
        if (open) {
            startCamera();
        }
        return () => stopCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Bring up the camera ONLY after the <video> element is in the DOM.
    // We drive getUserMedia ourselves (instead of ZXing's `decodeFromConstraints`)
    // for two reasons:
    //   1. We get real error names (NotAllowedError / NotFoundError / NotReadableError…)
    //      and can show a sensible message to the user instead of a silent black box.
    //   2. We can attach the stream to our own <video> element and call play()
    //      explicitly — iOS Safari often needs that even with the `autoplay` attribute.
    // We poll for videoRef.current via requestAnimationFrame because relying on
    // the effect timing alone has proven unreliable on iOS Safari production
    // builds (returns null even after commit).
    useEffect(() => {
        if (!scanning) return;

        let cancelled = false;
        let raf = null;
        let waitTries = 0;
        let stream = null;
        setCamStatus('waiting-for-video');

        const beginCamera = async (videoEl) => {
            setCamStatus('starting');
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new Error('UNSUPPORTED_BROWSER');
                }
                setCamStatus('requesting-permission');
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: 'environment' } },
                    audio: false,
                });
                if (cancelled) {
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }
                setCamStatus('stream-acquired');

                videoEl.srcObject = stream;
                videoEl.setAttribute('playsinline', 'true');
                videoEl.setAttribute('webkit-playsinline', 'true');
                videoEl.muted = true;

                setCamStatus('starting-playback');
                try {
                    await videoEl.play();
                } catch (_e1) {
                    await new Promise(r => setTimeout(r, 150));
                    try { await videoEl.play(); } catch (_e2) { /* ignore */ }
                }
                if (cancelled) return;
                setCamStatus('playing');

                const reader = new BrowserMultiFormatReader();
                readerRef.current = reader;
                reader.decodeFromVideoElement(videoEl, (result) => {
                    if (cancelled) return;
                    if (result) {
                        const text = result.getText();
                        if (text && /^\d{8,14}$/.test(text)) {
                            setScanning(false);
                            lookupEan(text);
                        }
                    }
                });
            } catch (e) {
                if (stream) { try { stream.getTracks().forEach(t => t.stop()); } catch (_e) { /* ignore */ } }
                if (cancelled) return;
                console.error('Camera start failed:', e);
                setScanning(false);
                const name = e?.name || e?.message || '';
                setCamStatus(`error:${name || 'unknown'}`);
                let msg;
                if (name === 'NotAllowedError' || name === 'SecurityError') {
                    msg = language === 'de'
                        ? 'Kamera-Zugriff verweigert. iOS: Einstellungen → Safari → Kamera = Erlauben.'
                        : 'Camera denied. iOS: Settings → Safari → Camera = Allow.';
                } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
                    msg = language === 'de'
                        ? 'Keine geeignete Kamera gefunden. Bitte EAN manuell eingeben.'
                        : 'No suitable camera found. Please enter EAN manually.';
                } else if (name === 'NotReadableError') {
                    msg = language === 'de'
                        ? 'Kamera wird gerade von einer anderen App genutzt. App schließen und erneut versuchen.'
                        : 'Camera is in use by another app. Close it and try again.';
                } else if (name === 'UNSUPPORTED_BROWSER') {
                    msg = language === 'de'
                        ? 'Dein Browser unterstützt keinen Kamera-Zugriff. Bitte EAN manuell eingeben.'
                        : 'Browser does not support camera access. Please enter EAN manually.';
                } else {
                    msg = (language === 'de' ? 'Kamera-Fehler: ' : 'Camera error: ') + (e?.message || name || 'unknown');
                }
                setError(msg);
            }
        };

        // Wait for the <video> element to actually be in the DOM (RAF-poll
        // because callback refs would be cleaner but the existing JSX uses
        // the useRef pattern and we don't want to risk regression).
        const waitForVideo = () => {
            if (cancelled) return;
            if (videoRef.current) {
                beginCamera(videoRef.current);
                return;
            }
            waitTries += 1;
            if (waitTries > 60) {
                setCamStatus('error:video-ref-never-attached');
                setScanning(false);
                setError(language === 'de'
                    ? 'Video-Element konnte nicht initialisiert werden. Bitte App-Cache leeren und erneut versuchen.'
                    : 'Could not initialise video element. Please clear app cache and retry.');
                return;
            }
            raf = requestAnimationFrame(waitForVideo);
        };
        waitForVideo();

        return () => {
            cancelled = true;
            if (raf) cancelAnimationFrame(raf);
            if (stream) {
                try { stream.getTracks().forEach(t => t.stop()); } catch (_e) { /* ignore */ }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanning]);

    const handleClose = () => {
        stopCamera();
        setScanning(false);
        setProduct(null);
        setUnknownEan(null);
        setAiResult(null);
        setError(null);
        setManualEan('');
        setCamStatus('idle');
        onOpenChange(false);
    };

    // Manual EAN entry (fallback when camera not available)
    const handleManualLookup = (e) => {
        e.preventDefault();
        const ean = manualEan.trim();
        if (!/^\d{8,14}$/.test(ean)) {
            toast.error(language === 'de' ? 'Ungültiger Code — 8 bis 14 Ziffern' : 'Invalid code — 8 to 14 digits');
            return;
        }
        lookupEan(ean);
    };

    // Vision-AI fallback: user uploads a label photo; backend extracts macros
    const handleAiPhotoUpload = async (file) => {
        if (!file) return;
        setAiAnalyzing(true);
        try {
            const reader = new FileReader();
            reader.onload = async () => {
                const base64 = reader.result.split(',')[1];
                const res = await axios.post(
                    `${API}/api/nutrition/analyze/image`,
                    { image_base64: base64 },
                    { timeout: 60000 }
                );
                const data = res.data || {};
                // Normalise into our product shape. analyze/image returns
                // { name, items: [{ calories, protein, carbs, fat, portion }, ...] }.
                // For barcode-label use case, we aggregate first item or totals.
                const totalCals = data.calories ?? data.total_calories ?? (data.items?.[0]?.calories) ?? 0;
                const totalProtein = data.protein ?? (data.items?.[0]?.protein) ?? null;
                const totalCarbs = data.carbs ?? (data.items?.[0]?.carbs) ?? null;
                const totalFat = data.fat ?? (data.items?.[0]?.fat) ?? null;
                const portionG = parseInt(((data.items?.[0]?.portion) || '100').match(/\d+/)?.[0] || '100', 10);
                // Scale to per-100g
                const factor = portionG > 0 ? 100 / portionG : 1;
                const guessed = {
                    name: data.name || data.food_name || (language === 'de' ? 'Foto-Schätzung' : 'Photo estimate'),
                    brand: null,
                    kcal_100g: Math.round(totalCals * factor) || null,
                    protein_100g: totalProtein != null ? Math.round(totalProtein * factor * 10) / 10 : null,
                    carbs_100g: totalCarbs != null ? Math.round(totalCarbs * factor * 10) / 10 : null,
                    fat_100g: totalFat != null ? Math.round(totalFat * factor * 10) / 10 : null,
                    source: 'vision_ai',
                };
                setAiResult(guessed);
                setProduct(guessed);
                setGrams(100);
                setUnknownEan(null);
            };
            reader.readAsDataURL(file);
        } catch (e) {
            toast.error(language === 'de' ? 'Foto-Analyse fehlgeschlagen' : 'Photo analysis failed');
        } finally {
            setAiAnalyzing(false);
        }
    };

    // Persist the chosen portion as a food log + save the product for next time
    const handleLog = async () => {
        if (!product) return;
        setSubmitting(true);
        try {
            const factor = grams / 100;
            const calories = Math.round((product.kcal_100g || 0) * factor);
            const protein = Math.round((product.protein_100g || 0) * factor * 10) / 10;
            const carbs = Math.round((product.carbs_100g || 0) * factor * 10) / 10;
            const fat = Math.round((product.fat_100g || 0) * factor * 10) / 10;

            // 1) Log the meal
            const foodLabel = product.brand ? `${product.brand} ${product.name}` : product.name;
            await axios.post(`${API}/api/nutrition/food`, {
                name: `${foodLabel} (${grams}g)`,
                calories,
                protein,
                carbs,
                fat,
                meal_type: mealType,
            });

            // 2) Persist to custom DB for instant next-scan (only if we have an ean)
            if (product.ean || product.source === 'vision_ai' || aiResult) {
                try {
                    await axios.post(`${API}/api/nutrition/custom-food`, {
                        ean: product.ean,
                        name: product.name,
                        brand: product.brand,
                        kcal_100g: product.kcal_100g,
                        protein_100g: product.protein_100g,
                        carbs_100g: product.carbs_100g,
                        fat_100g: product.fat_100g,
                        serving_quantity_g: product.serving_quantity_g,
                        image_url: product.image_url,
                        source: product.source || 'open_food_facts',
                    });
                } catch (_persist) { /* non-fatal */ }
            }
            toast.success(language === 'de' ? '✓ Im Tagebuch gespeichert' : '✓ Logged to diary');
            onLogged?.();
            handleClose();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === 'string'
                ? detail
                : Array.isArray(detail)
                    ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
                    : (e?.message || (language === 'de' ? 'Speichern fehlgeschlagen' : 'Save failed'));
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const calcedCalories = product?.kcal_100g != null ? Math.round((product.kcal_100g) * grams / 100) : null;

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
            <SheetContent side="bottom" className="h-[92vh] p-0 flex flex-col rounded-t-2xl">
                <SheetHeader className="p-4 border-b">
                    <SheetTitle className="flex items-center gap-2 text-base">
                        <ScanLine className="w-4 h-4 text-primary" />
                        {language === 'de' ? 'Barcode scannen' : 'Scan barcode'}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="barcode-scanner-body">
                    {/* Camera viewfinder (scanning mode) */}
                    {scanning && !product && !unknownEan && !error && (
                        <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3]">
                            <video
                                ref={videoRef}
                                className="w-full h-full object-cover"
                                playsInline
                                muted
                                autoPlay
                            />
                            {/* Sucher-Overlay */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-3/4 h-1/3 border-2 border-white/80 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
                            </div>
                            <div className="absolute bottom-3 left-0 right-0 text-center text-white text-xs">
                                {language === 'de' ? 'Barcode in den Rahmen halten…' : 'Hold barcode in frame…'}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/40 text-sm space-y-3">
                            <p className="text-destructive">{error}</p>
                            <form onSubmit={handleManualLookup} className="flex gap-2">
                                <Input
                                    value={manualEan}
                                    onChange={(e) => setManualEan(e.target.value)}
                                    placeholder={language === 'de' ? 'EAN manuell eingeben' : 'Enter EAN manually'}
                                    inputMode="numeric"
                                    data-testid="barcode-manual-input"
                                />
                                <Button type="submit" data-testid="barcode-manual-submit">
                                    {language === 'de' ? 'Suchen' : 'Lookup'}
                                </Button>
                            </form>
                        </div>
                    )}

                    {/* Unknown product — AI Vision fallback */}
                    {unknownEan && !product && (
                        <div className="space-y-3 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50">
                            <div>
                                <p className="font-semibold flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-amber-600" />
                                    {language === 'de' ? 'Produkt nicht in Datenbank gefunden' : 'Product not in database'}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">EAN: {unknownEan}</p>
                            </div>
                            <p className="text-sm">
                                {language === 'de'
                                    ? 'Lade ein Foto vom Etikett hoch — Nanouks Vision-KI liest die Nährwerte aus.'
                                    : 'Upload a label photo — Nanouk\'s Vision-AI will read the nutrition info.'}
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => handleAiPhotoUpload(e.target.files?.[0])}
                                data-testid="barcode-ai-photo-input"
                            />
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={aiAnalyzing}
                                className="w-full"
                                data-testid="barcode-ai-photo-btn"
                            >
                                {aiAnalyzing ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'de' ? 'KI analysiert…' : 'AI analyzing…'}</>
                                ) : (
                                    <><Camera className="w-4 h-4 mr-2" />{language === 'de' ? 'Etikett fotografieren' : 'Photograph label'}</>
                                )}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => { setUnknownEan(null); startCamera(); }}
                                className="w-full"
                                data-testid="barcode-retry-btn"
                            >
                                {language === 'de' ? '↻ Erneut scannen' : '↻ Scan again'}
                            </Button>
                        </div>
                    )}

                    {/* Resolved product preview */}
                    {product && (
                        <div className="space-y-4" data-testid="barcode-product-card">
                            <div className="flex gap-3 p-3 rounded-2xl bg-muted/40 border">
                                {product.image_url && (
                                    <img src={product.image_url} alt="" className="w-20 h-20 rounded-xl object-cover bg-background" />
                                )}
                                <div className="flex-1 min-w-0">
                                    {product.brand && <p className="text-xs text-muted-foreground">{product.brand}</p>}
                                    <p className="font-semibold truncate">{product.name}</p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                                        <span>{product.kcal_100g ?? '—'} kcal</span>
                                        <span>P {product.protein_100g ?? '—'}g</span>
                                        <span>C {product.carbs_100g ?? '—'}g</span>
                                        <span>F {product.fat_100g ?? '—'}g</span>
                                        <span className="opacity-60">/100g</span>
                                    </div>
                                    {product.source === 'vision_ai' && (
                                        <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                                            <Sparkles className="w-2.5 h-2.5" />
                                            {language === 'de' ? 'KI-Schätzung — bitte prüfen' : 'AI estimate — please verify'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs">
                                    {language === 'de' ? 'Menge in Gramm' : 'Amount in grams'}
                                </Label>
                                <div className="flex gap-2">
                                    {[50, 100, 150, 200].map(g => (
                                        <Button
                                            key={g}
                                            type="button"
                                            variant={grams === g ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setGrams(g)}
                                            className="flex-1"
                                            data-testid={`barcode-grams-${g}`}
                                        >
                                            {g}g
                                        </Button>
                                    ))}
                                </div>
                                <Input
                                    type="number"
                                    min={1}
                                    max={2000}
                                    value={grams}
                                    onChange={(e) => setGrams(parseInt(e.target.value, 10) || 0)}
                                    data-testid="barcode-grams-input"
                                />
                                {product.serving_quantity_g && (
                                    <button
                                        type="button"
                                        onClick={() => setGrams(product.serving_quantity_g)}
                                        className="text-xs text-primary underline"
                                    >
                                        {language === 'de' ? `1 Portion = ${product.serving_quantity_g}g` : `1 serving = ${product.serving_quantity_g}g`}
                                    </button>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs">
                                    {language === 'de' ? 'Mahlzeit' : 'Meal'}
                                </Label>
                                <div className="grid grid-cols-4 gap-2">
                                    {[
                                        { id: 'breakfast', de: 'Frühst.', en: 'Brk' },
                                        { id: 'lunch', de: 'Mittag', en: 'Lunch' },
                                        { id: 'dinner', de: 'Abend', en: 'Dinner' },
                                        { id: 'snack', de: 'Snack', en: 'Snack' },
                                    ].map(m => (
                                        <Button
                                            key={m.id}
                                            type="button"
                                            variant={mealType === m.id ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setMealType(m.id)}
                                            data-testid={`barcode-meal-${m.id}`}
                                        >
                                            {language === 'de' ? m.de : m.en}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 text-sm">
                                <p className="font-semibold">
                                    {language === 'de' ? 'Für diese Portion:' : 'For this portion:'}
                                </p>
                                <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs">
                                    <span><strong>{calcedCalories}</strong> kcal</span>
                                    <span>P {product.protein_100g != null ? Math.round(product.protein_100g * grams / 10) / 10 : '—'}g</span>
                                    <span>C {product.carbs_100g != null ? Math.round(product.carbs_100g * grams / 10) / 10 : '—'}g</span>
                                    <span>F {product.fat_100g != null ? Math.round(product.fat_100g * grams / 10) / 10 : '—'}g</span>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => { setProduct(null); setAiResult(null); setUnknownEan(null); startCamera(); }}
                                    className="flex-1"
                                    data-testid="barcode-rescan-btn"
                                >
                                    {language === 'de' ? '↻ Erneut scannen' : '↻ Scan again'}
                                </Button>
                                <Button
                                    onClick={handleLog}
                                    disabled={submitting || !calcedCalories}
                                    className="flex-1"
                                    data-testid="barcode-log-btn"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                    {language === 'de' ? 'Hinzufügen' : 'Add'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-3 border-t flex items-center justify-between">
                    <p className="text-[11px] text-muted-foreground">
                        {language === 'de' ? 'Open Food Facts + Nanouk Vision' : 'Open Food Facts + Nanouk Vision'}
                    </p>
                    <Button variant="ghost" size="sm" onClick={handleClose} data-testid="barcode-close-btn">
                        <X className="w-4 h-4 mr-1" />
                        {language === 'de' ? 'Schließen' : 'Close'}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
